import { Component } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/SelfIncrementingProgressbar/SelfIncrementingProgressbar.css'
import { shallowEqual } from 'tangle-shared'
import { QAMarked } from 'interfaces/component'

type SelfIncrementingProgressbarStateProps = {
  initial: number
  increment: number
  maximum: number
}

type SelfIncrementingProgressbarDispatchProps = {
  onFinish: () => void
  onUnmount: (progress: number) => void
}

type SelfIncrementingProgressbarOwnProps =
  & QAMarked
  & {
    isCompact?: boolean
    hideValue?: boolean
  }

export type SelfIncrementingProgressbarProps =
  & SelfIncrementingProgressbarStateProps
  & SelfIncrementingProgressbarDispatchProps
  & SelfIncrementingProgressbarOwnProps

type SelfIncrementingProgressbarState = {
  progress: number
}

export const PROGRESSBAR_TICK = 200 // milliseconds

class SelfIncrementingProgressbar extends Component<SelfIncrementingProgressbarProps, SelfIncrementingProgressbarState> {
  private intervalId: number | null = null

  constructor (props: SelfIncrementingProgressbarProps, context: any) {
    super(props, context)

    this.state = {
      progress: props.initial,
    }
  }

  render () {
    const { isCompact, qaType, qaMarker, hideValue } = this.props
    const { progress } = this.state

    const className = cn(styles.selfIncrementingProgressbar, {
      [styles.large]: !isCompact,
    })

    const flooredProgress = Math.floor(progress)

    const progressValue = !hideValue && (
      <div className={ styles.progressPercentage }>
        { flooredProgress }%
      </div>
    )

    return (
      <div className={ className } data-qa-type={ qaType } data-qa-marker={ qaMarker }>
        { progressValue }
        <div className={ styles.progressLineWrapper }>
          <div className={ styles.progressLine } style={{ transform: `translateX(${ flooredProgress }%)` }} />
        </div>
      </div>
    )
  }

  componentDidMount () {
    this.initiateProgress()
  }

  componentDidUpdate (prevProps: SelfIncrementingProgressbarProps) {
    if (shallowEqual(this.props, prevProps)) {
      return
    }

    this.killInterval()
    this.initiateProgress()
  }

  componentWillUnmount () {
    this.killInterval()
    this.props.onUnmount(this.state.progress)
  }

  private killInterval = () => {
    if (this.intervalId) {
      window.clearInterval(this.intervalId)
    }
    this.intervalId = null
  }

  private incrementProgress = () => {
    const { increment, maximum, onFinish } = this.props
    const { progress } = this.state
    const nextProgress = Math.min(progress + increment, maximum)

    this.setState({
      progress: nextProgress,
    }, () => {
      // we have reached the maximum, need to kill interval
      if (nextProgress === maximum) {
        this.killInterval()
      }

      // we have reached the maximum of the maximum (100%)
      if (nextProgress >= 100) {
        onFinish()
      }
    })
  }

  private initiateProgress = () => {
    const { maximum } = this.props
    const { progress } = this.state

    if (progress > maximum) {
      return
    }

    this.intervalId = window.setInterval(
      this.incrementProgress,
      PROGRESSBAR_TICK
    )
  }
}

export default SelfIncrementingProgressbar
